"use client";

import type { Variants } from "motion/react";
import type { HTMLAttributes } from "react";

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { motion, useAnimation } from "motion/react";

interface ScanTextIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface ScanTextIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  loop?: boolean;
  animateOnMount?: boolean;
}

const frameVariants: Variants = {
  visible: { opacity: 1 },
  hidden: { opacity: 1 },
};

const lineVariants: Variants = {
  visible: { pathLength: 1, opacity: 1 },
  hidden: { pathLength: 0, opacity: 0 },
};

const ScanTextIcon = forwardRef<ScanTextIconHandle, ScanTextIconProps>(
  (
    {
      onMouseEnter,
      onMouseLeave,
      className,
      size = 28,
      loop = false,
      animateOnMount = false,
      ...props
    },
    ref,
  ) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: async () => {
          await controls.start((i) => ({
            pathLength: 0,
            opacity: 0,
            transition: { delay: i * 0.1, duration: 0.3 },
          }));
          await controls.start((i) => ({
            pathLength: 1,
            opacity: 1,
            transition: {
              delay: i * 0.1,
              duration: 0.3,
              repeat: loop ? Number.POSITIVE_INFINITY : 0,
              repeatType: loop ? "loop" : undefined,
              repeatDelay: loop ? 1 : 0,
            },
          }));
        },
        stopAnimation: () => void controls.start("visible"),
      };
    });

    useEffect(() => {
      if ((loop || animateOnMount) && !isControlledRef.current) {
        const runAnimation = async () => {
          await controls.start((i) => ({
            pathLength: 0,
            opacity: 0,
            transition: { delay: i * 0.1, duration: 0.3 },
          }));
          await controls.start((i) => ({
            pathLength: 1,
            opacity: 1,
            transition: {
              delay: i * 0.1,
              duration: 0.3,
              repeat: loop ? Number.POSITIVE_INFINITY : 0,
              repeatType: loop ? "loop" : undefined,
              repeatDelay: loop ? 1 : 0,
            },
          }));
        };
        void runAnimation();
      }
    }, [loop, animateOnMount, controls]);

    const handleMouseEnter = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isControlledRef.current && !loop) {
          await controls.start((i) => ({
            pathLength: 0,
            opacity: 0,
            transition: { delay: i * 0.1, duration: 0.3 },
          }));
          await controls.start((i) => ({
            pathLength: 1,
            opacity: 1,
            transition: { delay: i * 0.1, duration: 0.3 },
          }));
        } else {
          onMouseEnter?.(e);
        }
      },
      [controls, onMouseEnter, loop],
    );

    const handleMouseLeave = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isControlledRef.current && !loop) {
          await controls.start("visible");
        } else {
          onMouseLeave?.(e);
        }
      },
      [controls, onMouseLeave, loop],
    );

    return (
      <div
        className={twMerge(className)}
        onMouseEnter={(e) => void handleMouseEnter(e)}
        onMouseLeave={(e) => void handleMouseLeave(e)}
        {...props}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <motion.path variants={frameVariants} d="M3 7V5a2 2 0 0 1 2-2h2" />
          <motion.path variants={frameVariants} d="M17 3h2a2 2 0 0 1 2 2v2" />
          <motion.path variants={frameVariants} d="M21 17v2a2 2 0 0 1-2 2h-2" />
          <motion.path variants={frameVariants} d="M7 21H5a2 2 0 0 1-2-2v-2" />
          <motion.path
            variants={lineVariants}
            initial="visible"
            animate={controls}
            custom={0}
            d="M7 8h8"
          />
          <motion.path
            variants={lineVariants}
            initial="visible"
            animate={controls}
            custom={1}
            d="M7 12h10"
          />
          <motion.path
            variants={lineVariants}
            initial="visible"
            animate={controls}
            custom={2}
            d="M7 16h6"
          />
        </svg>
      </div>
    );
  },
);

ScanTextIcon.displayName = "ScanTextIcon";

export { ScanTextIcon };
